import { Box, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowRightLeft, Calculator, Globe, LogOut, Settings } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/modules/common/application/context/language';
import useLogout from '@/modules/common/application/use-logout';
import useNumberFormat from '@/modules/common/application/use-number-format';
import { FeatureFlag, LanguageCode, NumberFormat } from '@/modules/common/types';

const PublicFooter = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { logout } = useLogout();
  const { language, setLanguage } = useLanguage();
  const { numberFormat, setNumberFormat } = useNumberFormat();
  const navigate = useNavigate();
  const isMavue2Enabled = useFeatureFlagEnabled(FeatureFlag.MAVUE_2);
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        width: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Divider sx={{ mb: 1 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          pb: 2,
        }}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full ml-4">
              <Settings className="h-[1.2rem] w-[1.2rem]" />
              <span className="sr-only">{t('modules.dataManagement.publicFooter.settings')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 ml-4">
            <DropdownMenuLabel>
              {t('modules.dataManagement.publicFooter.settings')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                <Globe className="h-4 w-4" /> {t('modules.dataManagement.publicFooter.language')}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setLanguage(LanguageCode.EN)}
                className={cn('cursor-pointer', language === LanguageCode.EN && 'bg-accent')}
              >
                {t('modules.dataManagement.publicFooter.english')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setLanguage(LanguageCode.DE)}
                className={cn('cursor-pointer', language === LanguageCode.DE && 'bg-accent')}
              >
                {t('modules.dataManagement.publicFooter.german')}
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                <Calculator className="h-4 w-4" />{' '}
                {t('modules.dataManagement.publicFooter.numberFormat')}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setNumberFormat(NumberFormat.COMMA_PERIOD)}
                className={cn(
                  'cursor-pointer',
                  numberFormat === NumberFormat.COMMA_PERIOD && 'bg-accent'
                )}
              >
                {t('modules.dataManagement.publicFooter.periodComma')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setNumberFormat(NumberFormat.PERIOD_COMMA)}
                className={cn(
                  'cursor-pointer',
                  numberFormat === NumberFormat.PERIOD_COMMA && 'bg-accent'
                )}
              >
                {t('modules.dataManagement.publicFooter.commaPeriod')}
              </DropdownMenuItem>
            </DropdownMenuGroup>

            {isMavue2Enabled && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => navigate('/reporting')}
                    className="cursor-pointer flex items-center gap-2"
                  >
                    <ArrowRightLeft className="h-4 w-4" />
                    {t('modules.dataManagement.publicFooter.switchToMavue2', 'Switch to Mavue 2.0')}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => logout()}
              className="text-destructive cursor-pointer flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" /> {t('modules.dataManagement.publicFooter.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Box>
    </Box>
  );
};

export default PublicFooter;
